import { apiClient } from '../utils/api-client';
import type {
  Announcement,
  AnnouncementResponse,
  AnnouncementsListResponse,
} from '../types/announcement';

export interface AnnouncementPayload {
  title: string;
  description?: string;
  link_url?: string;
  link_text?: string;
  pinned?: boolean;
  image_signed_id?: string;
  /** ISO timestamp or null to clear. */
  starts_at?: string | null;
  /** ISO timestamp or null to clear. */
  ends_at?: string | null;
}

/** Thrown when the gym already has the maximum number of announcements. */
export class AnnouncementLimitError extends Error {
  /** Title of the announcement that would be evicted to make room. */
  oldestTitle?: string;

  constructor(message: string, oldestTitle?: string) {
    super(message);
    this.name = 'AnnouncementLimitError';
    this.oldestTitle = oldestTitle;
  }
}

interface ApiErrorBody {
  errors?: Array<{ detail: string; code?: string; meta?: { oldest_title?: string } }>;
}

async function throwForResponse(response: Response, fallback: string): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
  const error = body.errors?.[0];
  if (error?.code === 'announcement_limit_reached') {
    throw new AnnouncementLimitError(error.detail || fallback, error.meta?.oldest_title);
  }
  throw new Error(error?.detail || fallback);
}

class AnnouncementsService {
  async list(gymSlug: string): Promise<Announcement[]> {
    const response = await apiClient.get(`/api/gyms/${gymSlug}/announcements`);
    if (!response.ok) {
      throw new Error('Failed to fetch announcements');
    }
    const data: AnnouncementsListResponse = await response.json();
    return data.data.map(item => item.attributes);
  }

  async create(
    gymSlug: string,
    payload: AnnouncementPayload,
    options: { replaceOldest?: boolean } = {}
  ): Promise<Announcement> {
    const body: { data: { attributes: AnnouncementPayload }; replace_oldest?: boolean } = {
      data: { attributes: payload },
    };
    if (options.replaceOldest) {
      body.replace_oldest = true;
    }
    const response = await apiClient.post(`/api/gyms/${gymSlug}/announcements`, body);
    if (!response.ok) {
      await throwForResponse(response, 'Failed to create announcement');
    }
    const data: AnnouncementResponse = await response.json();
    return data.data.attributes;
  }

  async update(gymSlug: string, id: string, payload: AnnouncementPayload): Promise<Announcement> {
    const response = await apiClient.patch(
      `/api/gyms/${gymSlug}/announcements/${id}`,
      { data: { attributes: payload } }
    );
    if (!response.ok) {
      await throwForResponse(response, 'Failed to update announcement');
    }
    const data: AnnouncementResponse = await response.json();
    return data.data.attributes;
  }

  async remove(gymSlug: string, id: string): Promise<void> {
    const response = await apiClient.delete(`/api/gyms/${gymSlug}/announcements/${id}`);
    if (!response.ok) {
      throw new Error('Failed to delete announcement');
    }
  }
}

export const announcementsService = new AnnouncementsService();
